import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { PagedCombobox } from "@/components/ui/pagedcombobox";
import { X } from "lucide-react";
import { customersApi } from "../services/customersApi";

interface CustomerPickerProps {
  /** Selected customer id. Empty string means no selection. */
  value: string;
  onChange: (customerId: string) => void;
  disabled?: boolean;
  /** Singular noun shown in the trigger and search input ("Select {title}"). */
  title?: string;
  /** When true, show a × button next to the picker to clear the selection. */
  allowClear?: boolean;
  /** Restrict the picker to active customers. Defaults to true. */
  activeOnly?: boolean;
}

type PickerItem = { id: string; name: string };

/**
 * Searchable + paginated customer picker. Hits /Customers on every debounced
 * keystroke and pages on demand — never preloads the full customer list.
 *
 * When `value` is set externally (e.g. opened with an initial customer), the
 * picker fetches that customer once to populate its trigger label.
 */
export const CustomerPicker = ({
  value,
  onChange,
  disabled = false,
  title = "Customer",
  allowClear = false,
  activeOnly = true,
}: CustomerPickerProps) => {
  const [selection, setSelection] = useState<PickerItem | null>(null);

  useEffect(() => {
    if (!value) {
      setSelection(null);
      return;
    }
    if (selection?.id === value) return;
    let cancelled = false;
    customersApi
      .getCustomerById(value)
      .then((c) => {
        if (!cancelled) setSelection({ id: c.id, name: c.customerName });
      })
      .catch(() => {
        // Leave selection as-is; the picker will still allow re-selection.
      });
    return () => {
      cancelled = true;
    };
    // We intentionally only resync when the external value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const fetchPage = useCallback(
    async ({
      search,
      pageNumber,
      pageSize,
    }: {
      search: string;
      pageNumber: number;
      pageSize: number;
    }) => {
      const result = await customersApi.getCustomers({
        search,
        pageNumber,
        pageSize,
        isActive: activeOnly ? true : undefined,
        sortBy: "customerName",
      });
      return {
        items: result.items.map((c) => ({ id: c.id, name: c.customerName })),
      };
    },
    [activeOnly],
  );

  const handleChange = (item: PickerItem | null) => {
    setSelection(item);
    onChange(item?.id ?? "");
  };

  return (
    <div className="flex items-center gap-1">
      <div className="flex-1 min-w-0">
        <PagedCombobox<PickerItem>
          title={title}
          value={selection}
          onChange={handleChange}
          fetchPage={fetchPage}
          disabled={disabled}
        />
      </div>
      {allowClear && selection && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => handleChange(null)}
          disabled={disabled}
          aria-label={`Clear ${title}`}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
